import type { IconMeaning } from '@bms2/shared';
import { Size } from '@bms2/shared';
import { useId } from 'react';
import { Icon } from '../Icon';
import styles from './IconButton.module.css';

/**
 * 🔒 The icon button (piece 4; T178): one control for every affordance whose
 * whole label is its glyph. The product owner asked how many one-off pieces the
 * screens carry that ought to be a function, and this was one of the answers —
 * the inventory of 2026-09-22 found eight hand-built icon affordances, each with
 * its own box, its own hover and its own idea of whether it had an accessible
 * name.
 *
 * 🔒 The accessible name is required, because an icon on its own says nothing to
 * a reader who cannot see it. The caller passes it through `t()`.
 *
 * 🔨 It is not a Button with an icon and no text. A Button carries a label and a
 * split action; this carries a glyph inside a square touch target, which is why
 * the two are separate controls rather than one control with a mode. 🔒 Both say
 * why they cannot be pressed (decision 1453): a greyed glyph that says nothing
 * is a button that looks broken.
 *
 * 🔨 It draws no chrome of its own until it is hovered. A row of bordered
 * squares reads as a toolbar, and every place this control stands — a banner, a
 * segmented control's end, a list row — already has a ground of its own.
 */
export type IconButtonTone = 'plain' | 'onFill' | 'accent' | 'destructive' | 'badge' | 'warning';

/**
 * 🔨 `fit` is for a control the layout around it sizes, and it is the one size
 * that is not a token: the avatar's camera badge lands on a diagonal computed
 * from the avatar's own radius, so a fixed square there would leave the circle.
 */
export type IconButtonSize = Size.Small | Size.Medium | 'fit';

export interface IconButtonProps {
  /** What the button does, as the shared icon reference names it. */
  meaning: IconMeaning;
  /** 🔒 The accessible name, through `t()`; an icon button without one is refused by the types. */
  label: string;
  /** Small is the row and the segmented control's end; Medium is the banner and a page's own bar; `fit` takes the box its caller placed it in. */
  size?: IconButtonSize;
  /**
   * Where it stands: `plain` on a light surface, `onFill` on the banner's own
   * gradient and the header's dark ground, `accent` where the act is the
   * primary one, `destructive` where it removes something, `warning` where the glyph is itself the warning a
   * row carries and pressing it goes to put it right: the Marks column's triangle (decision 9720; T3164).
   */
  tone?: IconButtonTone;
  /** The act. A button carries this; a link carries `href` instead, and exactly one of the two is given. */
  onClick?: () => void;
  /** A destination rather than an act; it draws as an anchor so the browser's own open-in-new-tab works. */
  href?: string;
  disabled?: boolean;
  /**
   * 🔒 Why it cannot be pressed, through `t()` (decision 1453), read while it is
   * disabled: the pointer's title in place of the name, and the reader's
   * description, the way Button says it (decision 0559).
   */
  disabledReason?: string;
  /** What a suite reads the control by, spelled by the caller: `data-icon-button` carries it. */
  name?: string;
  /** Announced pressed state, for a button that toggles something rather than doing it once. */
  pressed?: boolean;
  /** Given on a button that opens a menu: it announces the menu, and whether the menu stands open. */
  menuOpen?: boolean;
  /** The element's id, for a window it opens to hand the focus back to as it closes. */
  id?: string;
}

export const iconButtonDocumentedStates = ['plain', 'onFill', 'accent', 'destructive', 'badge', 'disabled', 'link', 'warning'] as const;

const BOX: Readonly<Record<string, string>> = { [Size.Small]: 'small', [Size.Medium]: 'medium', fit: 'fit' };

export function IconButton({ meaning, label, size = Size.Medium, tone = 'plain', onClick, href, disabled = false, disabledReason, name, pressed, menuOpen, id }: IconButtonProps) {
  const reasonId = useId();
  const className = [styles.button, styles[BOX[size] ?? 'medium']!, styles[tone]].join(' ');
  const glyph = <Icon meaning={meaning} size={size === Size.Medium ? 'header' : 'inline'} />;
  const shared = {
    className,
    'aria-label': label,
    title: label,
    'data-icon-button': name ?? meaning,
    'data-icon-button-tone': tone,
    ...(id === undefined ? {} : { id }),
  } as const;
  /* 🔨 A destination is an anchor and an act is a button: a div with a click handler is neither, and a person holding a key to open a second tab reaches nothing. */
  if (href !== undefined) {
    return <a {...shared} href={href}>{glyph}</a>;
  }
  const showsReason = disabled && disabledReason !== undefined && disabledReason !== '';
  const button = (
    <button
      type="button"
      {...shared}
      disabled={disabled}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      {...(menuOpen === undefined ? {} : { 'aria-haspopup': 'menu' as const, 'aria-expanded': menuOpen })}
      {...(showsReason ? { title: disabledReason, 'data-disabled-reason': disabledReason, 'aria-describedby': reasonId } : {})}
      onClick={onClick}
    >
      {glyph}
    </button>
  );
  if (!showsReason) return button;
  return (
    <>
      {button}
      <span className={styles.reason} id={reasonId}>
        {disabledReason}
      </span>
    </>
  );
}
